import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';
import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';

import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { RemixContentComponent } from '@atlassian/malleable-ui-components/editor-remix-creator';
import {
	isAllowedMBEExtensionForRemix,
	isRemixRestrictedNodeType,
} from '@atlassian/malleable-ui-components/remix/remix-config';
import type {
	RemixContentComponentProps,
	RemixFinalizeSuccessPayload,
	RemixInsertSuccessPayload,
	RemixProductConfig,
	RemixProductIntegration,
} from '@atlassian/malleable-ui-components/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useSmartCreationModalActions,
	useSmartCreationModalState,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	Message,
	publishMessage,
} from '@confluence/page-tree-refresh-state-container/entry-points/messageBus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';
import { FeedbackVariant } from '@confluence/smart-create/entry-points/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '@confluence/smart-create/entry-points/useLazyShowFeedbackFlag.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import { EditorPageRemixModal as ObjectRemixComponent } from './PageRemixContainer.tsx';
import { useShouldHideDiagramRemixOption } from './useShouldHideDiagramRemixOption.ts';

const REMIX_MBE_ALLOW_LIST: ReadonlyArray<string> = ['native-tabs'];
const REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST: ReadonlySet<string> = new Set([
	'native-embed:page',
]);

export const isNodeUnsupportedForConfluenceRemix = (node: ADFEntity): boolean => {
	if (node.type === 'multiBodiedExtension') {
		return !isAllowedMBEExtensionForRemix(node.attrs?.extensionKey, REMIX_MBE_ALLOW_LIST);
	}

	if (
		node.type === 'extension' &&
		typeof node.attrs?.extensionKey === 'string' &&
		REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST.has(node.attrs.extensionKey) &&
		fg('maui_remixable_page_native_embeds')
	) {
		return false;
	}

	return isRemixRestrictedNodeType(node.type);
};

const confluenceProductConfig: RemixProductConfig = {
	productKey: 'confluence',
	analyticsProductName: 'confluence',
	getRemixGeneratePreviewMetadata: ({ type }) =>
		type === 'infographic'
			? {
					isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
				}
			: {},
	isNodeUnsupportedForRemix: isNodeUnsupportedForConfluenceRemix,
	shouldUseEphemeralPreview: (chatMessages: StoreChatItem[]): boolean => {
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_PREPARE') {
				return message.action ? isRemixGeneratedFlow(message.action) : false;
			}
		}
		return false;
	},
	isActionReady: (chatMessages: StoreChatItem[]): boolean => {
		const hasWhiteboard =
			findLastAssistantAction(
				chatMessages,
				(action) =>
					action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
					action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
			) !== undefined;
		if (hasWhiteboard) {
			return true;
		}
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_COMPLETE') {
				return true;
			}
			if (message.type === 'HUMAN') {
				return false;
			}
		}
		return false;
	},
};

const useConfluenceProductIntegration = ({
	flags,
	onFinalizeFailed,
	rootPageTreeParentId,
}: {
	flags: FlagsStateContainer;
	onFinalizeFailed?: RemixProductIntegration['onFinalizeFailed'];
	rootPageTreeParentId?: string;
}): RemixProductIntegration => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { openModal, closeModal } = useSmartCreationModalActions();
	const { toggleConversationAssistant: toggleAssistant } = useToggleConversationAssistant();
	const experienceTracker = useAIMateExperienceTracker();
	const { dynamicUiType, dynamicUiSubtype } = useStagingAreaState();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const pageMode = useGetPageMode();
	const { cloudId } = useSessionData();
	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	const onRemixInsertSuccess = useCallback(
		({ currentConversationId, remixMetadata, requestId }: RemixInsertSuccessPayload) => {
			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant: FeedbackVariant.AFTER_REMIX_INSERT,
				contentType,
				contentMode: contentType === 'page' ? pageMode : undefined,
				cloudId,
				currentConversationId,
				remixMetadata,
				requestId,
			});
		},
		[showCreateWithRovoModalCloseFeedbackFlag, contentType, pageMode, cloudId],
	);

	const publishPageTreeRefresh = useCallback(
		(parentId: string | undefined) => {
			const pageTreeParentId = parentId ?? rootPageTreeParentId;
			if (!pageTreeParentId) {
				return;
			}

			publishMessage(Message.PageTreeRefreshRequested, {
				parentId: pageTreeParentId,
			});
		},
		[rootPageTreeParentId],
	);

	const onFinalizeSuccess = useCallback(
		({ parentId }: RemixFinalizeSuccessPayload) => {
			if (!expValEquals('cc_maui_polish_changes_batch_3', 'isEnabled', true)) {
				return;
			}

			publishPageTreeRefresh(parentId);
		},
		[publishPageTreeRefresh],
	);

	return useMemo<RemixProductIntegration>(
		() => ({
			contentType: contentType ?? 'page',
			isRemixPreviewVisible: showSmartCreationModal,
			onRemixInsertSuccess,
			onFinalizeSuccess,
			onFinalizeFailed,
			openRemixPreview: ({ isFloating, remixConversationId }) => {
				const remixAifcHandoffAttributes = {
					featureArea: 'remix' as const,
					entryPoint: 'editor' as const,
					...(remixConversationId && { conversationChannelId: remixConversationId }),
					...(dynamicUiType && { remixType: dynamicUiType }),
					...(dynamicUiSubtype && { remixSubtype: dynamicUiSubtype }),
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					id: ExperienceName.REMIX_AIFC_HANDOFF,
					attributes: remixAifcHandoffAttributes,
				});
				openModal({
					entryPoint: 'dynamicUi',
					spaceKey: spaceKey ?? undefined,
					stagingAreaActive: true,
					isFloating,
					shouldShowBackButton: false,
					onOpenCallback: () => {
						experienceTracker.succeed({
							name: ExperienceName.REMIX_AIFC_HANDOFF,
							attributes: remixAifcHandoffAttributes,
						});
					},
					...(remixConversationId && expValEquals('cc-maui-experiment', 'isEnabled', true)
						? { remixConversationId }
						: {}),
					content: contentId
						? {
								id: contentId,
								type: contentType ?? 'page',
								subType: isLivePage ? 'live' : null,
						  }
						: undefined,
					// Pass submitCallback only for ephemeral (floating) previews so the full
					// modal flow (isFloating: false) still falls through to
					// handleSubmitSmartCreate and redirects to the newly created content.
					// Mirrors useRemixToAIFC.
					...(isFloating
						? {
								submitCallback: async () => {
									if (
										contentId &&
										expVal('cc-maui-refresh-page-tree-with-diagram', 'isEnabled', false)
									) {
										publishPageTreeRefresh(contentId);
									}
								},
						  }
						: {}),
				});
			},
			closeRemixPreview: closeModal,
			toggleConversationAssistant: (location: string) => {
				toggleAssistant(location as RovoChatOpenMode);
			},
		}),
		[
			showSmartCreationModal,
			openModal,
			closeModal,
			toggleAssistant,
			spaceKey,
			contentId,
			contentType,
			isLivePage,
			onRemixInsertSuccess,
			onFinalizeSuccess,
			onFinalizeFailed,
			publishPageTreeRefresh,
			dynamicUiType,
			dynamicUiSubtype,
			experienceTracker,
		],
	);
};

const useConfig = () => {
	const { cloudId } = useSessionData();

	return useConversationAssistantConfig({
		assistanceServiceConfigHeaders: {
			'X-Cloudid': cloudId,
		},
		preventAbortRequestsOnStagingModalClose: true,
	});
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useConfigOld() {
	return undefined;
}

const useConfigSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useConfig,
	useConfigOld,
);

const useSmartCreationContext = (): SmartCreationContext => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: true,
	});

	const context: SmartCreationContext = {
		confluence: {
			space: spaceDetail
				? {
						id: spaceDetail.spaceId,
						name: spaceDetail.name,
						// `key` is needed to construct correct whiteboard URLs
						// (`/wiki/spaces/{spaceKey}/whiteboard/...`). Without it,
						// `useOnComplete` falls back to `~{numericSpaceId}`, which
						// the page-level URL routing treats as a personal-space key
						// and resolves to the wrong space.
						key: spaceKey ?? undefined,
						iconPath: spaceDetail.iconPath,
						typeSettings: spaceDetail.typeSettings,
				  }
				: undefined,
			parentContent: spaceDetail
				? {
						id: spaceDetail.homepage?.id ?? '',
						title: spaceDetail.homepage?.title ?? '',
						contentType: spaceDetail.homepage?.type ?? '',
				  }
				: undefined,
			content: contentId
				? {
						id: contentId,
						type: contentType ?? 'page',
						subType: isLivePage ? 'live' : null,
				  }
				: undefined,
		},
	};

	return context;
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useSmartCreationContextOld() {
	return undefined;
}

const useSmartCreationContextSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useSmartCreationContext,
	useSmartCreationContextOld,
);

const PlatformComponentInner = ({
	flags,
	...props
}: RemixContentComponentProps & WithFlagsProps) => {
	const smartCreationConfig = useConfigSafe();
	const smartCreationContext = useSmartCreationContextSafe();
	const productIntegration = useConfluenceProductIntegration({
		flags,
		onFinalizeFailed: props.onFinalizeFailed,
		rootPageTreeParentId: smartCreationContext?.confluence?.parentContent?.id,
	});
	const hiddenOptions = useShouldHideDiagramRemixOption();

	return (
		<RemixContentComponent
			{...props}
			entryPoint={fg('platform_editor_ai_remix_in_editor_loading') ? 'dynamicUi' : undefined}
			smartCreationConfig={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationConfig : undefined
			}
			smartCreationContext={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationContext : undefined
			}
			hiddenOptions={
				hiddenOptions.length > 0
					? [...(props.hiddenOptions ?? []), ...hiddenOptions]
					: props.hiddenOptions
			}
			productConfig={confluenceProductConfig}
			productIntegration={productIntegration}
		/>
	);
};

const PlatformComponent = withFlags(PlatformComponentInner);

export const createRemixContentComponentPlatform = (): ((
	props: RemixContentComponentProps,
) => React.JSX.Element | null) => {
	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		const isObjectRemix = props.remixType === 'object';

		useLayoutEffect(() => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				return;
			}
			if (
				isSmartCreationModalOpen.current &&
				!showSmartCreationModal &&
				isThinking &&
				(!isObjectRemix || !fg('maui_remixable_page_native_embeds'))
			) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking, isObjectRemix]);

		let remixContent;
		if (isObjectRemix) {
			const { remixType, adfDocument, ...restProps } = props;

			remixContent = fg('maui_remixable_page_native_embeds') ? (
				<ObjectRemixComponent {...restProps} remixType={remixType} adfDocument={adfDocument} />
			) : (
				<PlatformComponent {...restProps} remixType="block" />
			);
		} else {
			remixContent = <PlatformComponent {...props} />;
		}

		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			return <ConfluenceConversationAssistant>{remixContent}</ConfluenceConversationAssistant>;
		}

		if (!(isModalOpen || isThinking)) {
			return null;
		}

		return <ConfluenceConversationAssistant>{remixContent}</ConfluenceConversationAssistant>;
	};

	return Wrapper;
};
